import { cardLabel, cardName, isRed, RANK_NAMES, RANKS, SUIT_NAMES, SUITS, } from '../cards.js';
import { IllegalMoveError, } from './types.js';
/** Ace low, King high — the order foundations build in. */
export function rankIndex(card) {
    return RANKS.indexOf(card.rank);
}
const ACE = 0;
function alternatingColours(a, b) {
    return isRed(a.suit) !== isRed(b.suit);
}
/** The tableau builds down in alternating colours, exactly as Klondike does. */
export function canStack(moving, onto) {
    return alternatingColours(moving, onto) && rankIndex(moving) === rankIndex(onto) - 1;
}
/** Foundations build up from the Ace, one suit each. */
export function canPlaceOnFoundation(card, foundation) {
    const top = foundation[foundation.length - 1];
    if (!top)
        return rankIndex(card) === ACE;
    return card.suit === top.suit && rankIndex(card) === rankIndex(top) + 1;
}
/** Is this slice a descending, alternating-colour run? */
export function isValidRun(cards) {
    for (let i = 1; i < cards.length; i++) {
        if (!canStack(cards[i], cards[i - 1]))
            return false;
    }
    return true;
}
// ---------------------------------------------------------------------------
// How much can move at once
// ---------------------------------------------------------------------------
/**
 * The supermove limit: `(free cells + 1) × 2 ^ (empty columns)`.
 *
 * This is not a rule of FreeCell so much as arithmetic about one. Moving a run
 * of `n` really means parking `n − 1` cards somewhere and fetching them back,
 * and this is exactly how many that shuffling can manage. Computer versions
 * collapse the whole sequence into one drag; `supermoves: false` makes the
 * player do it by hand, which is why the limit drops to a single card.
 *
 * An empty column you are moving *into* cannot also be a staging post, so it
 * does not count towards the doubling — a subtlety that decides a surprising
 * number of endgames.
 */
export function capacityFor(freeCellsEmpty, emptyColumns, rules, intoEmptyColumn) {
    if (!rules.supermoves)
        return 1;
    const staging = Math.max(0, emptyColumns - (intoEmptyColumn ? 1 : 0));
    return (freeCellsEmpty + 1) * 2 ** staging;
}
export function emptyCellCount(state) {
    return state.free.filter((cell) => cell === null).length;
}
export function emptyColumnCount(state) {
    return state.tableau.filter((column) => column.length === 0).length;
}
/** How many cards may land on `to` in one move. */
export function moveCapacity(state, to) {
    const intoEmpty = to?.zone === 'tableau' && (state.tableau[to.index]?.length ?? 0) === 0;
    return capacityFor(emptyCellCount(state), emptyColumnCount(state), state.rules, intoEmpty);
}
// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------
/**
 * The cards a source refers to, top of the carried run first.
 *
 * Throws rather than returning null, matching the other two engines — callers
 * are already inside a try/catch that turns this into a protocol error.
 */
export function resolveSource(state, from) {
    switch (from.zone) {
        case 'free': {
            const card = state.free[from.index];
            if (card === undefined)
                throw new IllegalMoveError('That cell does not exist.');
            if (card === null)
                throw new IllegalMoveError('That cell is empty.');
            return [card];
        }
        case 'foundation': {
            if (!state.rules.allowFoundationToTableau) {
                throw new IllegalMoveError('Cards cannot come back off the foundations.');
            }
            const pile = state.foundations[from.suit];
            const card = pile[pile.length - 1];
            if (!card)
                throw new IllegalMoveError('That foundation is empty.');
            return [card];
        }
        case 'tableau': {
            const column = state.tableau[from.index];
            if (!column)
                throw new IllegalMoveError('That column does not exist.');
            if (from.depth < 1 || from.depth > column.length) {
                throw new IllegalMoveError('There are not that many cards in that column.');
            }
            const run = column.slice(column.length - from.depth);
            if (!isValidRun(run))
                throw new IllegalMoveError('Those cards are not in sequence.');
            return run;
        }
    }
}
/** Can `cards` (a validated run) legally land on `to`? */
export function canPlace(state, cards, to) {
    const lead = cards[0];
    if (!lead)
        return false;
    if (to.zone === 'foundation') {
        // Foundations take one card at a time, and only of their own suit.
        if (cards.length !== 1 || lead.suit !== to.suit)
            return false;
        return canPlaceOnFoundation(lead, state.foundations[to.suit]);
    }
    if (to.zone === 'free') {
        if (cards.length !== 1)
            return false;
        return state.free[to.index] === null;
    }
    const column = state.tableau[to.index];
    if (!column)
        return false;
    if (cards.length > moveCapacity(state, to))
        return false;
    const onto = column[column.length - 1];
    // Any card opens an empty column — FreeCell has no King rule.
    return onto ? canStack(lead, onto) : true;
}
/**
 * Moves that would change nothing.
 *
 * Cell to cell is included: the cells are interchangeable, so shuffling a card
 * between them is not a move, and letting it count would mean a dead position
 * never registered as dead.
 */
function isNoop(from, to) {
    if (from.zone === 'tableau' && to.zone === 'tableau')
        return from.index === to.index;
    if (from.zone === 'free' && to.zone === 'free')
        return true;
    if (from.zone === 'foundation' && to.zone === 'foundation')
        return from.suit === to.suit;
    return false;
}
/** A banked card is on its way home; the cells are not a stop on that route. */
function isFoundationToCell(from, to) {
    return from.zone === 'foundation' && to.zone === 'free';
}
export function validateMove(state, from, to) {
    if (state.phase !== 'playing')
        throw new IllegalMoveError('The game is over.');
    if (isNoop(from, to))
        throw new IllegalMoveError('That card is already there.');
    if (isFoundationToCell(from, to)) {
        throw new IllegalMoveError('A banked card only comes back down to the tableau.');
    }
    const cards = resolveSource(state, from);
    if (!canPlace(state, cards, to))
        throw new IllegalMoveError('That card cannot go there.');
    return cards;
}
// ---------------------------------------------------------------------------
// Enumeration
// ---------------------------------------------------------------------------
function pushIfLegal(state, moves, from, to, cards) {
    if (isNoop(from, to) || isFoundationToCell(from, to))
        return;
    if (canPlace(state, cards, to))
        moves.push({ kind: 'move', from, to, cards: cards.slice() });
}
/**
 * Every move available right now.
 *
 * Deliberately exhaustive rather than clever: the board is tiny, and the client
 * uses this both to light up movable cards and to decide whether the deal is
 * dead. Foundation moves are listed first so a UI that takes the first match on
 * a double-click banks the card.
 */
export function legalMoves(state) {
    const moves = [];
    if (state.phase !== 'playing')
        return moves;
    const sources = [];
    state.free.forEach((card, index) => {
        if (card)
            sources.push({ from: { zone: 'free', index }, cards: [card] });
    });
    state.tableau.forEach((column, index) => {
        // Every depth is a candidate: the whole run can move, and so can any
        // sub-run hanging off the exposed end of it.
        for (let depth = 1; depth <= column.length; depth++) {
            const run = column.slice(column.length - depth);
            if (!isValidRun(run))
                break;
            sources.push({ from: { zone: 'tableau', index, depth }, cards: run });
        }
    });
    if (state.rules.allowFoundationToTableau) {
        for (const suit of SUITS) {
            const pile = state.foundations[suit];
            const top = pile[pile.length - 1];
            if (top)
                sources.push({ from: { zone: 'foundation', suit }, cards: [top] });
        }
    }
    for (const { from, cards } of sources) {
        const lead = cards[0];
        if (cards.length === 1) {
            pushIfLegal(state, moves, from, { zone: 'foundation', suit: lead.suit }, cards);
            for (let index = 0; index < state.free.length; index++) {
                pushIfLegal(state, moves, from, { zone: 'free', index }, cards);
            }
        }
        for (let index = 0; index < state.tableau.length; index++) {
            pushIfLegal(state, moves, from, { zone: 'tableau', index }, cards);
        }
    }
    return moves;
}
/** Every card is on a foundation. */
export function isWon(state) {
    return SUITS.every((suit) => state.foundations[suit].length === RANKS.length);
}
/**
 * Is the deal genuinely dead?
 *
 * Unlike Klondike this needs no guesswork, and no over-approximation to stay
 * on the safe side: nothing is face down and there is no stock, so the legal
 * move list is the whole truth. Every cell full with nothing that will stack
 * is the end of it.
 */
export function isDeadlocked(state) {
    if (state.phase !== 'playing')
        return false;
    if (isWon(state))
        return false;
    return legalMoves(state).length === 0;
}
/** The suit-keyed empty foundations a fresh game starts with. */
export function emptyFoundations() {
    return { S: [], H: [], D: [], C: [] };
}
/** The exposed cards: one per cell, plus the end of each column. */
function bankableCards(state) {
    const cards = [];
    for (const cell of state.free)
        if (cell)
            cards.push(cell);
    for (const column of state.tableau) {
        const top = column[column.length - 1];
        if (top)
            cards.push(top);
    }
    return cards;
}
/**
 * Would banking everything bankable, over and over, clear the board?
 *
 * Klondike offers its finish once nothing is face down, which is a proxy for
 * "already won". FreeCell has no face-down cards to use as a proxy, so this
 * asks the question directly and simulates it out. The board is 52 cards, so
 * the honest answer is cheaper than a heuristic would be.
 *
 * The test is that nothing is left rather than that the foundations reach the
 * King, which is the same thing in a real deal and stays true of a position a
 * test built by hand out of part of a pack.
 */
export function canAutoComplete(state) {
    if (state.phase !== 'playing')
        return false;
    if (isWon(state))
        return false;
    const heights = {
        S: state.foundations.S.length,
        H: state.foundations.H.length,
        D: state.foundations.D.length,
        C: state.foundations.C.length,
    };
    const free = state.free.filter((card) => card !== null);
    const columns = state.tableau.map((column) => column.slice());
    let moved = true;
    while (moved) {
        moved = false;
        for (let i = free.length - 1; i >= 0; i--) {
            const card = free[i];
            if (rankIndex(card) !== heights[card.suit])
                continue;
            heights[card.suit] += 1;
            free.splice(i, 1);
            moved = true;
        }
        for (const column of columns) {
            const top = column[column.length - 1];
            if (!top || rankIndex(top) !== heights[top.suit])
                continue;
            heights[top.suit] += 1;
            column.pop();
            moved = true;
        }
    }
    return free.length === 0 && columns.every((column) => column.length === 0);
}
/** "a black Nine" — what a given card is waiting for underneath it. */
function wanted(onto) {
    const needed = RANKS[rankIndex(onto) - 1];
    if (!needed)
        return 'nothing — an Ace is the end of the line here';
    return `a ${isRed(onto.suit) ? 'black' : 'red'} ${RANK_NAMES[needed]}`;
}
/** Why `cards` will not go onto `to`. */
export function describeRefusal(ctx, cards, to) {
    const lead = cards[0];
    if (!lead)
        return 'There is nothing there to move.';
    if (to.zone === 'foundation') {
        if (cards.length > 1)
            return 'Foundations take one card at a time.';
        if (lead.suit !== to.suit) {
            return `${cardLabel(lead)} is not a ${SUIT_NAMES[to.suit].toLowerCase().replace(/s$/, '')}.`;
        }
        const top = ctx.foundations.find((f) => f.suit === to.suit)?.top ?? null;
        if (!top)
            return `The ${SUIT_NAMES[to.suit]} foundation starts with the Ace.`;
        const needed = RANKS[rankIndex(top) + 1];
        if (!needed)
            return `The ${SUIT_NAMES[to.suit]} foundation is already home.`;
        return `${SUIT_NAMES[to.suit]} is up to the ${RANK_NAMES[top.rank]} — the ${RANK_NAMES[needed]} is next.`;
    }
    if (to.zone === 'free') {
        if (cards.length > 1)
            return 'A cell holds one card, not a run.';
        // Which leaves an occupied cell as the only reason a single card bounces
        // off one. Where the card came *from* can also disqualify it — a banked
        // card, say — but this has only the destination to go on, so the board
        // answers that case before it gets here.
        return 'That cell is taken.';
    }
    const column = ctx.tableau[to.index];
    if (!column)
        return 'That column is not there.';
    // Capacity is checked before the stacking rule, because a run that would fit
    // perfectly well but cannot be carried is the refusal players find baffling.
    const cellsEmpty = ctx.free.filter((cell) => cell === null).length;
    const emptyColumns = ctx.tableau.filter((c) => c.length === 0).length;
    const capacity = capacityFor(cellsEmpty, emptyColumns, ctx.rules, column.length === 0);
    if (cards.length > capacity) {
        if (!ctx.rules.supermoves) {
            return 'Runs move one card at a time in this game — take the top card first.';
        }
        const room = capacity === 1 ? 'one card' : `${capacity} cards`;
        return `Only ${room} can move at once with ${cellsEmpty === 0 ? 'no' : cellsEmpty} cell${cellsEmpty === 1 ? '' : 's'} free. Empty a cell or a column first.`;
    }
    const onto = column[column.length - 1];
    if (!onto)
        return 'That card cannot go there.';
    if (rankIndex(lead) !== rankIndex(onto) - 1) {
        return `${cardLabel(onto)} takes ${wanted(onto)}.`;
    }
    // Right rank, so the colour is what is wrong.
    return `${cardLabel(lead)} is the wrong colour — ${cardLabel(onto)} takes ${wanted(onto)}.`;
}
/** Why `cards` cannot be picked up at all, wherever they were headed. */
export function describeUnliftable(cards) {
    const lead = cards[0];
    if (!lead)
        return 'There is nothing there to move.';
    if (cards.length > 1 && !isValidRun(cards)) {
        return 'Only a run in sequence and alternating colours moves as one.';
    }
    return `Nothing will take the ${cardName(lead)} yet.`;
}
//# sourceMappingURL=legal.js.map